/**
 * Material transaction drawer — receive / issue / adjust / return / scrap /
 * transfer + ledger + issuances (InventoryConsole workflow on current chrome).
 */
import { useEffect, useState } from "react";
import {
  ghostBtnClass,
  mfgPrimaryBtnClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  listManufacturingInventoryChangelog,
  postMaterialCostEntry,
} from "@/lib/manufacturing/costEntriesClient";
import {
  adjustManufacturingMaterialOnHand,
  completeManufacturingInterPlantTransfer,
  revalueManufacturingMaterialOnHand,
  createManufacturingInterPlantTransfer,
  reorderThresholdFromMetadata,
  returnManufacturingMaterial,
  scrapManufacturingMaterial,
} from "@/lib/manufacturing/inventoryFloorClient";
import {
  createManufacturingMaterialIssuance,
  listManufacturingMaterialIssuances,
  type MfgMaterialIssuanceSummary,
} from "@/lib/manufacturing/materialIssuanceClient";
import {
  listManufacturingPlants,
  listManufacturingUnits,
  type MfgMaterial,
  type MfgPlant,
  type MfgUnit,
} from "@/lib/manufacturingClient";
import { ManufacturingInventoryMaterialDrawerHistory } from "./ManufacturingInventoryMaterialDrawerHistory";
import {
  ManufacturingInventoryMaterialTxForm,
  type InventoryTxAction,
} from "./ManufacturingInventoryMaterialTxForm";
import { ManufacturingInventoryMaterialReorderField } from "./ManufacturingInventoryMaterialReorderField";

const usd = (n: number) =>
  "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ACTIONS: Array<{ id: InventoryTxAction; label: string }> = [
  { id: "receive", label: "Receive" },
  { id: "issue", label: "Issue" },
  { id: "adjust", label: "Adjust" },
  { id: "reprice", label: "Reprice" },
  { id: "return", label: "Return" },
  { id: "scrap", label: "Scrap" },
  { id: "transfer", label: "Transfer" },
];

export function ManufacturingInventoryMaterialDrawer({
  material,
  onClose,
  onChanged,
}: {
  material: MfgMaterial;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [mat, setMat] = useState(material);
  const [action, setAction] = useState<InventoryTxAction>("receive");
  const [qty, setQty] = useState("");
  const [cost, setCost] = useState("");
  const [newBal, setNewBal] = useState("");
  const [note, setNote] = useState("");
  const [targetUnitId, setTargetUnitId] = useState("");
  const [toPlantId, setToPlantId] = useState("");
  const [units, setUnits] = useState<MfgUnit[]>([]);
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [ledger, setLedger] = useState<
    Array<{ id: string; entry_type: string; quantity: number; unit_cost: number; notes: string }>
  >([]);
  const [issuances, setIssuances] = useState<MfgMaterialIssuanceSummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const reorder = reorderThresholdFromMetadata(mat.metadata);

  useEffect(() => {
    setMat(material);
  }, [material]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [logRes, issRes, unitsRes, plantsRes] = await Promise.all([
        listManufacturingInventoryChangelog({ material_id: mat.id, limit: 20 }),
        listManufacturingMaterialIssuances({ material_id: mat.id }),
        listManufacturingUnits({ plant_id: mat.plant_id }),
        listManufacturingPlants(),
      ]);
      if (cancelled) return;
      if (logRes.ok) {
        setLedger(
          (logRes.data.entries || []).map((r) => ({
            id: r.id,
            entry_type: r.entry_type,
            quantity: r.quantity,
            unit_cost: r.unit_cost,
            notes: r.notes,
          })),
        );
      }
      if (issRes.ok) setIssuances(issRes.data.events);
      if (unitsRes.ok) {
        setUnits((unitsRes.data.units || []).filter((u) => u.serial || u.build_started_at));
      }
      if (plantsRes.ok) {
        setPlants((plantsRes.data.plants || []).filter((p) => p.id !== mat.plant_id && p.is_active));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mat.id, mat.plant_id, toast]);

  async function submit() {
    setError(null);
    setBusy(true);
    const q = parseFloat(qty);

    if (action === "adjust") {
      const nb = parseFloat(newBal);
      if (!Number.isFinite(nb) || nb < 0) {
        setBusy(false);
        setError("Enter a valid new balance");
        return;
      }
      const r = await adjustManufacturingMaterialOnHand({
        material_id: mat.id,
        on_hand_qty: nb,
        notes: note || undefined,
      });
      setBusy(false);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setMat((m) => ({ ...m, on_hand_qty: r.data.on_hand_qty }));
      setToast(`Adjusted to ${r.data.on_hand_qty} ${mat.unit}`);
      setNewBal("");
      onChanged();
      return;
    }

    if (action === "reprice") {
      const c = parseFloat(cost);
      if (!Number.isFinite(c) || c < 0) {
        setBusy(false);
        setError("Enter a unit price");
        return;
      }
      const r = await revalueManufacturingMaterialOnHand({
        material_id: mat.id,
        unit_cost: c,
        notes: note || undefined,
      });
      setBusy(false);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setMat((m) => ({ ...m, unit_cost: r.data.unit_cost }));
      setToast(
        r.data.unchanged
          ? `On-hand price already ${usd(r.data.unit_cost)}`
          : `On-hand price set to ${usd(r.data.unit_cost)}`,
      );
      setCost("");
      onChanged();
      return;
    }

    if (!Number.isFinite(q) || q <= 0) {
      setBusy(false);
      setError("Enter a quantity");
      return;
    }

    if (action === "receive") {
      const c = parseFloat(cost);
      if (!Number.isFinite(c) || c <= 0) {
        setBusy(false);
        setError("Enter a unit cost");
        return;
      }
      const r = await postMaterialCostEntry({
        material_id: mat.id,
        quantity: q,
        unit_cost: c,
        entry_type: "receive",
        notes: note || "Received",
      });
      setBusy(false);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setToast(`Received ${q} ${mat.unit}`);
      setQty("");
      setCost("");
      onChanged();
      return;
    }

    if (action === "issue") {
      const r = await createManufacturingMaterialIssuance({
        plant_id: mat.plant_id,
        material_id: mat.id,
        quantity: q,
        mfg_unit_id: targetUnitId || undefined,
      });
      setBusy(false);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setMat((m) => ({ ...m, on_hand_qty: Math.max(0, m.on_hand_qty - q) }));
      setToast(`Issued ${q} ${mat.unit}`);
      setQty("");
      onChanged();
      return;
    }

    if (action === "return") {
      const r = await returnManufacturingMaterial({
        material_id: mat.id,
        quantity: q,
        notes: note || undefined,
      });
      setBusy(false);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setMat((m) => ({ ...m, on_hand_qty: r.data.on_hand_qty }));
      setToast(`Returned ${q} ${mat.unit}`);
      setQty("");
      onChanged();
      return;
    }

    if (action === "scrap") {
      const r = await scrapManufacturingMaterial({
        material_id: mat.id,
        quantity: q,
        notes: note || undefined,
      });
      setBusy(false);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setMat((m) => ({ ...m, on_hand_qty: r.data.on_hand_qty }));
      setToast(`Scrapped ${q} ${mat.unit}`);
      setQty("");
      onChanged();
      return;
    }

    if (!toPlantId) {
      setBusy(false);
      setError("Pick a destination plant");
      return;
    }
    const created = await createManufacturingInterPlantTransfer({
      from_plant_id: mat.plant_id,
      to_plant_id: toPlantId,
      material_id: mat.id,
      quantity: q,
      notes: note || undefined,
    });
    if (!created.ok) {
      setBusy(false);
      setError(created.error);
      return;
    }
    const done = await completeManufacturingInterPlantTransfer(created.data.transfer.id);
    setBusy(false);
    if (!done.ok) {
      setError(done.error);
      return;
    }
    setMat((m) => ({ ...m, on_hand_qty: Math.max(0, m.on_hand_qty - q) }));
    setToast(`Transferred ${q} ${mat.unit}`);
    setQty("");
    onChanged();
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} aria-hidden />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col overflow-y-auto border-l border-gray-200 bg-white shadow-xl dark:border-zinc-700 dark:bg-zinc-950">
        <div className="sticky top-0 flex items-start gap-2 border-b border-gray-200 bg-white px-4 py-3 dark:border-zinc-700 dark:bg-zinc-950">
          <div className="min-w-0">
            <div className="font-mono text-lg font-bold text-gray-900 dark:text-zinc-50">{mat.code}</div>
            <div className="text-xs text-gray-500">
              {mat.name}
              {reorder != null ? ` · reorder ${reorder}` : " · no reorder qty"}
            </div>
          </div>
          <button type="button" className={`${ghostBtnClass} ml-auto`} onClick={onClose}>
            Close
          </button>
        </div>

        <div className="flex flex-col gap-4 p-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <Metric label="On hand" value={`${mat.on_hand_qty} ${mat.unit}`} />
            <Metric label="WAC" value={usd(mat.unit_cost)} />
            <Metric label="Value" value={usd(mat.on_hand_qty * mat.unit_cost)} />
          </div>
          <ManufacturingInventoryMaterialReorderField
            material={mat}
            onMaterial={setMat}
            onChanged={onChanged}
          />
          {error ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
          ) : null}
          {toast ? (
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              {toast}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-1.5">
            {ACTIONS.map((a) => (
              <button
                key={a.id}
                type="button"
                className={action === a.id ? mfgPrimaryBtnClass : mfgSecondaryBtnClass}
                onClick={() => setAction(a.id)}
              >
                {a.label}
              </button>
            ))}
          </div>

          <ManufacturingInventoryMaterialTxForm
            mat={mat}
            action={action}
            qty={qty}
            setQty={setQty}
            cost={cost}
            setCost={setCost}
            newBal={newBal}
            setNewBal={setNewBal}
            note={note}
            setNote={setNote}
            targetUnitId={targetUnitId}
            setTargetUnitId={setTargetUnitId}
            toPlantId={toPlantId}
            setToPlantId={setToPlantId}
            units={units}
            plants={plants}
            busy={busy}
            onSubmit={() => void submit()}
          />

          <ManufacturingInventoryMaterialDrawerHistory
            unit={mat.unit}
            ledger={ledger}
            issuances={issuances}
          />
        </div>
      </aside>
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-200 px-2 py-2 dark:border-zinc-700">
      <div className="text-[10px] text-gray-500">{label}</div>
      <div className="font-mono text-sm font-bold text-gray-900 dark:text-zinc-50">{value}</div>
    </div>
  );
}
